import { Globe2 } from 'lucide-react'
import type { Ref } from 'react'
import { isHttpsRedirectUrl } from '@shared/lib/url/https-redirect-url'
import { ImageUploadField, SegmentedControl, TextField, Toggle } from '@shared/design-system/ui'
import { checkoutAccentHex, checkoutAccentLabels } from '@entities/product/api/product.contract'
import type {
  CheckoutAccent,
  CheckoutTemplate,
  ProductCheckout,
} from '@entities/product/api/product.contract'
import {
  controlStack,
  copy,
  fieldBlock,
  fieldLabel,
  globalMarketCard,
  globalMarketIcon,
  globalMarketPill,
  group,
  groupHead,
  groupHint,
  groupTitle,
  inlineRow,
  nodeTitle,
  swatch,
  swatchCaption,
  toggleCard,
  toggleGrid,
} from './CheckoutBuilder.css'

export type CheckoutConfigControlsProps = {
  checkout: ProductCheckout
  onChange: (input: Partial<ProductCheckout>) => void
  thankYouUrlRef?: Ref<HTMLInputElement>
  originalThankYouUrl?: string
}

const blockLabels = {
  timer: 'Cronômetro com expiração real (em breve)',
  reviews: 'Depoimentos (em breve)',
  bump: 'Order bump',
  security: 'Selos de segurança',
}

const fieldLabels = {
  name: 'Nome completo',
  email: 'E-mail',
  phone: 'Telefone',
  document: 'CPF',
}

const requiredFields = new Set<keyof ProductCheckout['fields']>(['email'])
const accentOptions: CheckoutAccent[] = ['green', 'blue', 'violet', 'orange']

export const thankYouUrlError = 'Informe uma URL completa, começando com https://.'

export function CheckoutConfigControls({
  checkout,
  onChange,
  thankYouUrlRef,
  originalThankYouUrl,
}: CheckoutConfigControlsProps) {
  return (
    <div className={controlStack}>
      <section className={group}>
        <div className={groupHead}>
          <span className={groupTitle}>Aparência</span>
          <p className={groupHint}>Nome, modelo, cor de destaque e banner do checkout.</p>
        </div>

        <TextField
          label="Nome do checkout"
          value={checkout.name}
          onChange={(event) => onChange({ name: event.target.value })}
        />

        <div className={fieldBlock}>
          <span className={fieldLabel}>Modelo</span>
          <SegmentedControl<CheckoutTemplate>
            value={checkout.template}
            options={[
              { label: 'Clássico', value: 'classic' },
              { label: 'Minimalista', value: 'minimal' },
            ]}
            onChange={(value) => onChange({ template: value })}
          />
        </div>

        <div className={fieldBlock}>
          <span className={fieldLabel}>Cor de destaque</span>
          <div className={inlineRow}>
            {accentOptions.map((accent) => (
              <button
                key={accent}
                type="button"
                className={swatch}
                data-selected={checkout.accent === accent}
                title={checkoutAccentLabels[accent]}
                aria-label={checkoutAccentLabels[accent]}
                aria-pressed={checkout.accent === accent}
                style={{ background: checkoutAccentHex[accent] }}
                onClick={() => onChange({ accent })}
              />
            ))}
            <span className={swatchCaption}>{checkoutAccentLabels[checkout.accent]}</span>
          </div>
        </div>

        <ImageUploadField
          label="Banner do checkout"
          kind="banner"
          value={checkout.bannerUrl}
          onChange={(bannerUrl) => onChange({ bannerUrl })}
          aspect="20 / 6"
          hint="ideal 1600x540"
          allowUrl
        />
      </section>

      <section className={group}>
        <div className={groupHead}>
          <span className={groupTitle}>Campos do formulário</span>
          <p className={groupHint}>Escolha o que o comprador preenche antes de pagar.</p>
        </div>
        <div className={toggleGrid}>
          {(['name', 'email', 'phone', 'document'] as const).map((key) => {
            const required = requiredFields.has(key)
            return (
              <div
                key={key}
                className={toggleCard}
                title={required ? 'Obrigatório para entrega e confirmação' : undefined}
              >
                <span className={nodeTitle}>{fieldLabels[key]}</span>
                <Toggle
                  checked={required || checkout.fields[key]}
                  disabled={required}
                  onChange={(checked) =>
                    onChange({ fields: { ...checkout.fields, [key]: checked, email: true } })
                  }
                  ariaLabel={required ? `${fieldLabels[key]} obrigatório` : fieldLabels[key]}
                />
              </div>
            )
          })}
        </div>
      </section>

      <section className={group}>
        <div className={groupHead}>
          <span className={groupTitle}>Blocos de conversão</span>
          <p className={groupHint}>Elementos de confiança e urgência exibidos no checkout.</p>
        </div>
        <div className={toggleGrid}>
          {(['timer', 'reviews', 'bump', 'security'] as const).map((key) => {
            const unavailable = key === 'timer' || key === 'reviews'
            return (
              <div
                key={key}
                className={toggleCard}
                title={
                  key === 'timer'
                    ? 'Disponível quando o preço tiver uma expiração real validada pelo servidor.'
                    : key === 'reviews'
                      ? 'Disponível quando houver avaliações reais configuradas para o produto.'
                      : undefined
                }
              >
                <span className={nodeTitle}>{blockLabels[key]}</span>
                <Toggle
                  checked={unavailable ? false : checkout.blocks[key]}
                  disabled={unavailable}
                  onChange={(checked) =>
                    onChange({ blocks: { ...checkout.blocks, [key]: checked } })
                  }
                  ariaLabel={blockLabels[key]}
                />
              </div>
            )
          })}
        </div>
      </section>

      <section className={group}>
        <div className={groupHead}>
          <span className={groupTitle}>Pós-compra</span>
          <p className={groupHint}>Para onde o comprador vai depois de pagar.</p>
        </div>
        <TextField
          ref={thankYouUrlRef}
          label="URL da página de obrigado"
          error={
            isHttpsRedirectUrl(checkout.thankYouUrl, originalThankYouUrl)
              ? undefined
              : thankYouUrlError
          }
          helperText="Opcional. Se preenchido, após a compra o comprador é redirecionado para esta página (com ?saleId=): use com o snippet do upsell na sua página."
          value={checkout.thankYouUrl}
          onChange={(event) => onChange({ thankYouUrl: event.target.value })}
        />
      </section>

      <div className={globalMarketCard}>
        <span className={globalMarketIcon} aria-hidden="true">
          <Globe2 size={18} />
        </span>
        <div>
          <span className={nodeTitle}>Mercado global ativo</span>
          <p className={copy}>
            O checkout usa a cotação real para converter automaticamente o preço base para a moeda
            do país do comprador. Exemplo: se seu produto custa R$ 100,00 e o dólar está em R$ 5,00,
            nos Estados Unidos o comprador vê US$ 20,00. Se a moeda local não estiver disponível no
            provedor de pagamento, usamos USD como fallback.
            <br />
            <strong>
              O valor liquidado ao vendedor pode variar ligeiramente por câmbio e taxas do provedor.
            </strong>
          </p>
        </div>
        <span className={globalMarketPill}>Sempre ativo</span>
      </div>
    </div>
  )
}
